import { defineStore } from 'pinia'
import {
  getCalendarios,
  getEjecuciones,
  getRutina,           // ← nuevo: carga rutina individual para obtener rutina_refacciones
  crearEjecucion,
  actualizarEjecucion,
  actualizarActividadEjecucion,
  actualizarRefaccionEjecucion,
  eliminarRefaccionEjecucion,
  agregarRefaccionEjecucion,
  agregarImagenEjecucion,
  eliminarImagenEjecucion,
  reprogramarActividad,
} from '@/utils/api_backend'

export const useEjecucionStore = defineStore('ejecucion', {
  state: () => ({
    // ── Lista de calendarios (panel izquierdo) ────────────────
    calendarios:      [],
    cargandoLista:    false,

    // ── Calendario y ejecución seleccionados ─────────────────
    calendarioActivo: null,   // CalendarioMantenimiento (enriquecido con rutina_refacciones)
    ejecucion:        null,   // EjecucionMantenimiento | null
    cargandoDetalle:  false,
    guardando:        false,

    // ── Filtros del panel izquierdo ───────────────────────────
    // Los filtros de columna (máquina, área, periodicidad, estatus) viven
    // como Sets en el componente (embudo de ColumnFilterMenu); aquí solo
    // se guarda lo que no depende de esa UI.
    filtroTexto:        '',
    mostrarCompletados: false,

    // ── Categoría activa ──────────────────────────────────────
    categoriaActiva:  'preventivo',
  }),

  getters: {
    // Solo aplica texto — los filtros de columna (máquina, área,
    // periodicidad, estatus) y el toggle "ver completados" se combinan
    // en el componente, que es quien conoce los Sets del embudo.
    calendariosFiltrados: (state) => {
      const q = state.filtroTexto.toLowerCase()
      return state.calendarios.filter(c => {
        return !q ||
          (c.maquina_nombre || '').toLowerCase().includes(q) ||
          (c.rutina_nombre  || '').toLowerCase().includes(q)
      })
    },

    conteoEstatus: (state) => {
      return state.calendarios.reduce((acc, c) => {
        acc[c.estatus] = (acc[c.estatus] || 0) + 1
        return acc
      }, {})
    },

    progresoActividades: (state) => {
      if (!state.ejecucion?.actividades) return { completadas: 0, total: 0, pct: 0 }
      const total       = state.ejecucion.actividades.length
      const completadas = state.ejecucion.actividades.filter(a => a.completado).length
      return { completadas, total, pct: total ? Math.round((completadas / total) * 100) : 0 }
    },

    estaFinalizada: (state) => !!state.ejecucion?.fecha_fin,
  },

  actions: {
    // ── Cargar lista de calendarios ───────────────────────────
    async cargarCalendarios(categoria = 'preventivo') {
      this.categoriaActiva = categoria
      this.cargandoLista   = true
      try {
        // La nueva estructura usa Área y Periodicidad en lugar de categoría/tipo.
        // Se cargan todos los calendarios activos sin filtrar por rutina_categoria
        // (campo eliminado del modelo). El filtrado se hace en el getter.
        this.calendarios = await getCalendarios({})
      } catch (err) {
        console.error('Error cargando calendarios:', err)
        throw err
      } finally {
        this.cargandoLista = false
      }
    },

    // ── Seleccionar calendario y cargar su ejecución ──────────
    async seleccionarCalendario(calendario) {
      this.calendarioActivo = calendario
      this.ejecucion        = null
      this.cargandoDetalle  = true
      try {
        // ── Enriquecer el calendario con rutina_refacciones ──
        // El endpoint de calendarios no devuelve rutina_refacciones,
        // solo viene en el endpoint de rutina individual. Se carga aquí
        // una sola vez al seleccionar para que el panel de refacciones funcione.
        if (calendario.rutina && !calendario.rutina_refacciones) {
          try {
            const rutina = await getRutina(calendario.rutina)
            this.calendarioActivo = {
              ...calendario,
              rutina_refacciones:  rutina.rutina_refacciones  || [],
              rutina_actividades:  rutina.rutina_actividades   || [],
              actividad_descripcion_map: Object.fromEntries(
                (rutina.rutina_actividades || []).map(ra => [
                  ra.id,
                  ra.actividad_descripcion || ''
                ])
              ),
            }
          } catch (err) {
            console.warn('No se pudo cargar la rutina para refacciones:', err)
            // Continuar sin refacciones si falla
          }
        }

        // ── Buscar ejecución existente ──
        const lista = await getEjecuciones({ calendario: calendario.id })
        this.ejecucion = lista.length > 0 ? lista[0] : null

        // ── Si no existe, crearla automáticamente ──
        if (!this.ejecucion) {
          this.ejecucion = await crearEjecucion({
            calendario:   calendario.id,
            maquina:      calendario.maquina,
            rutina:       calendario.rutina,
            fecha_inicio: new Date().toISOString(),
          })
          this._actualizarEstatusLocal(calendario.id, 'EN_PROCESO')
          await this._recargarEjecucion()
        }
      } catch (err) {
        console.error('Error cargando ejecución:', err)
      } finally {
        this.cargandoDetalle = false
      }
    },

    // ── Completar una actividad ───────────────────────────────
    async completarActividad(actividadId, observaciones = '') {
      if (!this.ejecucion) return
      this.guardando = true
      try {
        await actualizarActividadEjecucion(this.ejecucion.id, actividadId, {
          completado: true,
          observaciones,
        })
        await this._recargarEjecucion()
      } finally {
        this.guardando = false
      }
    },

    // ── Actualizar cantidad de refacción (upsert) ─────────────
    async actualizarRefaccion(refaccionId, cantidad) {
      if (!this.ejecucion) return
      this.guardando = true
      try {
        const existente = this.ejecucion.refacciones?.find(
          r => r.refaccion_id === refaccionId || r.refaccion === refaccionId
        )
        if (existente) {
          if (cantidad <= 0) {
            await eliminarRefaccionEjecucion(this.ejecucion.id, existente.id)
          } else {
            await actualizarRefaccionEjecucion(this.ejecucion.id, existente.id, { cantidad })
          }
        } else if (cantidad > 0) {
          await agregarRefaccionEjecucion(this.ejecucion.id, {
            refaccion: refaccionId,
            cantidad,
          })
        }
        await this._recargarEjecucion()
      } finally {
        this.guardando = false
      }
    },

    // ── Agregar imagen ────────────────────────────────────────
    async agregarImagen(archivo) {
      if (!this.ejecucion) return
      this.guardando = true
      try {
        await agregarImagenEjecucion(this.ejecucion.id, archivo)
        await this._recargarEjecucion()
      } finally {
        this.guardando = false
      }
    },

    // ── Eliminar imagen ───────────────────────────────────────
    async eliminarImagen(imagenId) {
      if (!this.ejecucion) return
      this.guardando = true
      try {
        await eliminarImagenEjecucion(this.ejecucion.id, imagenId)
        await this._recargarEjecucion()
      } finally {
        this.guardando = false
      }
    },

    // ── Finalizar ejecución ───────────────────────────────────
    async finalizarEjecucion({ notas, tiempoHoras, pendientesInfo, tienePendientes }) {
      if (!this.ejecucion) return
      this.guardando = true
      try {
        // Reprogramar actividades pendientes primero
        if (tienePendientes && pendientesInfo) {
          // El endpoint reprogramar-actividad espera el ID de CalendarioActividad,
          // pero pendientesInfo tiene IDs de EjecucionActividad.
          //
          // Puente: EjecucionActividad.rutina_actividad === CalendarioActividad.rutina_actividad
          // Construimos un mapa { rutina_actividad_id → CalendarioActividad.id }
          // a partir de las actividades del calendarioActivo (que sí son CalendarioActividad).
          const calActMap = Object.fromEntries(
            (this.calendarioActivo?.actividades || []).map(ca => [
              String(ca.rutina_actividad), ca.id
            ])
          )

          for (const [ejecActividadId, info] of Object.entries(pendientesInfo)) {
            if (!info.motivo || !info.nueva_fecha) continue

            // Encontrar la EjecucionActividad correspondiente para obtener su rutina_actividad
            const ejecActividad = this.ejecucion.actividades?.find(
              a => String(a.id) === String(ejecActividadId)
            )
            if (!ejecActividad) {
              console.warn(`EjecucionActividad ${ejecActividadId} no encontrada en ejecucion.actividades`)
              continue
            }

            // Resolver el ID de CalendarioActividad usando rutina_actividad como puente
            const calendarioActividadId = calActMap[String(ejecActividad.rutina_actividad)]
            if (!calendarioActividadId) {
              console.warn(`No se encontró CalendarioActividad para rutina_actividad ${ejecActividad.rutina_actividad}`)
              continue
            }

            try {
              await reprogramarActividad(
                this.calendarioActivo.id,
                calendarioActividadId,   // ← ID correcto: CalendarioActividad
                info.nueva_fecha,
                info.motivo
              )
            } catch (err) {
              console.error(`Error reprogramando CalendarioActividad ${calendarioActividadId}:`, err)
            }
          }
        }

        const payload = {
          notas,
          tiempo_ejecucion_horas: tiempoHoras ? Math.round(tiempoHoras * 100) / 100 : null,
        }
        // Solo se pone fecha_fin si todas las actividades están completas
        if (!tienePendientes) {
          payload.fecha_fin = new Date().toISOString()
        }

        await actualizarEjecucion(this.ejecucion.id, payload)
        await this._recargarEjecucion()

        const nuevoEstatus = tienePendientes ? 'EN_PROCESO' : 'COMPLETADO'
        this._actualizarEstatusLocal(this.calendarioActivo.id, nuevoEstatus)
      } finally {
        this.guardando = false
      }
    },

    // ── Filtros ───────────────────────────────────────────────
    setFiltroTexto(txt)        { this.filtroTexto        = txt || '' },
    toggleCompletados()        { this.mostrarCompletados = !this.mostrarCompletados },

    limpiarFiltros() {
      this.filtroTexto        = ''
      this.mostrarCompletados = false
    },

    // ── Helpers internos ──────────────────────────────────────
    async _recargarEjecucion() {
      if (!this.calendarioActivo?.id) return
      const lista = await getEjecuciones({ calendario: this.calendarioActivo.id })
      this.ejecucion = lista.length > 0 ? lista[0] : null
    },

    _actualizarEstatusLocal(calendarioId, nuevoEstatus) {
      const idx = this.calendarios.findIndex(c => c.id === calendarioId)
      if (idx !== -1) {
        this.calendarios[idx] = { ...this.calendarios[idx], estatus: nuevoEstatus }
      }
    },
  },
})